/**
 * Domain Enums for OpsMatrix Platform
 *
 * Centralized enum definitions for domain entities.
 * Use these enums instead of literal strings throughout the codebase.
 */

// ============================================================================
// CASE MANAGEMENT ENUMS
// Note: CasePriority and CaseStatus are defined as Zod schemas in dtos/case.dto.ts
// ============================================================================

/**
 * Case severity levels
 */
export enum CaseSeverity {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  INFO = 'info',
}

// ============================================================================
// ALERT & NOTIFICATION ENUMS
// ============================================================================

/**
 * Security/Compliance alert status
 */
export enum AlertStatus {
  ACTIVE = 'active',
  RESOLVED = 'resolved',
  DISMISSED = 'dismissed',
  FALSE_POSITIVE = 'false_positive',
}

/**
 * Alert severity levels (same as CaseSeverity but separate for clarity)
 */
export enum AlertSeverity {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  INFO = 'info',
}

// NotificationChannel is defined in notification.enums.ts

/**
 * Entity types for notification subscriptions
 */
export enum NotificationEntityType {
  CASE = 'Case',
  LOAN_APPLICATION = 'LoanApplication',
  DOCUMENT = 'Document',
  TASK = 'Task',
  USER = 'User',
  BUSINESS = 'Business',
}

// ============================================================================
// DOCUMENT & APPROVAL ENUMS
// ============================================================================

/**
 * Document review tabs/filters
 */
export enum DocumentTab {
  ALL = 'all',
  AI_APPROVED = 'ai_approved',
  AI_REJECTED = 'ai_rejected',
  AI_REVIEW = 'ai_review',
  HIGH_PRIORITY = 'high_priority',
}

// ============================================================================
// PROPOSAL & OFFER ENUMS
// ============================================================================

/**
 * Proposal status lifecycle
 */
export enum ProposalStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  VIEWED = 'viewed',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

/**
 * Proposal response actions
 */
export enum ProposalAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

/**
 * Pricing quote status
 */
export enum PricingStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

/**
 * Offer status lifecycle
 */
export enum OfferStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  LOCKED = 'locked',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  COUNTER_OFFER = 'counter_offer',
}

// ============================================================================
// INVITE ENUMS
// Note: InviteStatus and InviteChannel are defined in invites/index.ts
// ============================================================================

/**
 * Relationship types for co-applicants/guarantors
 */
export enum RelationshipType {
  SPOUSE = 'spouse',
  BUSINESS_PARTNER = 'business_partner',
  GUARANTOR = 'guarantor',
  OTHER = 'other',
}

// ============================================================================
// BORROWER/LOAN ENUMS
// ============================================================================

/**
 * Borrower entity type
 */
export enum BorrowerType {
  INDIVIDUAL = 'individual',
  BUSINESS = 'business',
}

// ============================================================================
// CONFIGURATION ENUMS
// ============================================================================

/**
 * Rule comparison operators for provisioning/validation
 */
export enum RuleOperator {
  EQUALS = 'equals',
  IN = 'in',
  CONTAINS = 'contains',
  NOT_EQUALS = 'not_equals',
  NOT_IN = 'not_in',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
}

/**
 * Expiry mode for tokens/sessions
 */
export enum ExpiryMode {
  DATE_ONLY = 'date_only',
  INACTIVITY_ONLY = 'inactivity_only',
  HYBRID = 'hybrid',
}

/**
 * Form input types for qualifiers/forms
 */
export enum InputType {
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  TEXT = 'text',
  NUMBER = 'number',
}

/**
 * Signup approval mode
 */
export enum ApprovalMode {
  AUTO = 'auto',
  MANUAL = 'manual',
  DISABLED = 'disabled',
}

// ============================================================================
// CONSUMER/ACCOUNT STATUS ENUMS
// (For Zod schema compatibility - lowercase values)
// ============================================================================

/**
 * Generic account status - lowercase for API compatibility
 * Use ConsumerStatusSchema from dtos/consumer.dto.ts for Zod validation
 */
export enum AccountStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

// ConsumerType and DEFAULT_CONSUMER_TYPE are defined in dtos/consumer.dto.ts

// ============================================================================
// TRIGGER RECIPIENT ENUMS
// ============================================================================

/**
 * Trigger recipient type - who receives notification triggers
 */
export enum TriggerRecipientType {
  FIELD = 'field',
  ROLES = 'roles',
  SPECIFIC = 'specific',
  CREATOR = 'creator',
  ASSIGNED = 'assigned',
}
